import {
  MAP_DISPLAY_MODES,
  MAP_LAYER_TYPES,
  NULL_GROUP_LABEL,
  PLOT_LABEL_ID,
  SCOUTING_COLOR,
  isResultsTab as isResultsTabMode,
} from "../../constants";
import {
  resetScouting,
  setColorScaleStep,
  setPlotLabelOpacity,
  setPlotLabelProperty,
  setPlotLabelsVisible,
  setPlotMapColoring,
  setPlotMapOpacity,
  setScoutingColorScaleStep,
  setSelectedAttribute,
  setSelectedTrait,
  upsertMapLayer,
} from "../../actions/resultMap";
import {
  selectAttributesIncludingExperimentsAndModalities,
  selectColorScale,
  selectHasResults,
  selectMergedTraits,
  selectScoutingColorScale,
} from "../../selectors/resultMap";
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";

import { Badge } from "reactstrap";
import { LayerIdentifier } from "../../components/LayerIdentifier/LayerIdentifier";
import PropTypes from "prop-types";
import { PropertySelect } from "../../components/PropertySelect/PropertySelect";
import ReactSlider from "react-slider";
import { CfSelect as Select } from "../../components/CfSelect";
import { WorldMapLegendGrad } from "./WorldMapLegendGrad";
import { format } from "date-fns";
import { useAttributeColor } from "../../hooks/useAttributeColor";
import { useGetPropertyCategory } from "../../hooks/useGetPropertyCategory";
import { useWMSColorScale } from "../../hooks/useWMSColorScale";

const LEGEND_MENU_PROPS = {
  menuPortalTarget: document.body,
  menuPosition: "fixed",
  styles: { menuPortal: (base) => ({ ...base, zIndex: 1100 }) },
};

const formatPlotLabel = (key) => {
  const label = key.replaceAll("_", " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const WorldMapLegendItem = ({ layer }) => {
  const [
    trait,
    scoutingDates,
    selectedScoutingTrait,
    mapDisplayMode,
    selectedAttribute,
    plotMapColoring,
    plotMapOpacity,
    plotLabelProperty,
    plotLabelOpacity,
    plotLabelsVisible,
  ] = useSelector(({ resultMap }) => [
    resultMap.selectedTrait,
    resultMap.scoutingDates,
    resultMap.selectedScoutingTrait,
    resultMap.mapDisplayMode,
    resultMap.selectedAttribute,
    resultMap.plotMapColoring,
    resultMap.plotMapOpacity,
    resultMap.plotLabelProperty,
    resultMap.plotLabelOpacity,
    resultMap.plotLabelsVisible,
  ]);
  const attributes = useSelector(
    selectAttributesIncludingExperimentsAndModalities
  );
  const getPropertyCategory = useGetPropertyCategory();
  // attributes holds every text-valued property, a trait with yes/no values
  // included: keep the attribute categories, as the select did before.
  const attributeKeys = useMemo(
    () =>
      Object.keys(attributes).filter(
        (key) => getPropertyCategory(key).attribute
      ),
    [attributes, getPropertyCategory]
  );

  const plotLabelOptions = useMemo(
    () => [
      { value: PLOT_LABEL_ID, label: "ID" },
      ...attributeKeys
        .filter((key) =>
          attributes[key].some((value) => value != null && value !== "")
        )
        .map((key) => ({ value: key, label: formatPlotLabel(key) }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ],
    [attributeKeys, attributes]
  );
  // The traits of the bottom bar's selector, minus the keys PropertySelect
  // always blacklists (x, y, latitude...).
  const traits = useSelector(selectMergedTraits);
  const traitKeys = useMemo(
    () => traits.map(({ technical_name }) => technical_name),
    [traits]
  );
  const isPlotMapMode = mapDisplayMode === MAP_DISPLAY_MODES.PLOT_MAP;
  const isFieldBookMode = mapDisplayMode === MAP_DISPLAY_MODES.FIELD_BOOK;
  // The Results tab covers both colourings (trait or attribute).
  const isResultsTab = isResultsTabMode(mapDisplayMode);
  const isResultsPlots = layer.type === MAP_LAYER_TYPES.GEOJSON && isResultsTab;
  const scoutingColorScale = useSelector(selectScoutingColorScale);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isColoringCollapsed, setIsColoringCollapsed] = useState(false);
  const dispatch = useDispatch();

  const traitColorScale = useSelector(selectColorScale);
  const wmsColorScale = useWMSColorScale();

  const getAttributeColor = useAttributeColor(selectedAttribute);

  // Plot Map keeps an attribute legend card, its colours on or off the plots.
  const attributeValues = useMemo(() => {
    if (
      !(isResultsTab || isPlotMapMode) ||
      !selectedAttribute ||
      !attributes[selectedAttribute]
    )
      return [];
    return attributes[selectedAttribute];
  }, [isResultsTab, isPlotMapMode, selectedAttribute, attributes]);

  // The trait card exists as soon as a trait is picked: its gradient may be
  // missing (every plot filtered out, no value on this date), and keying the
  // card on the gradient made the legend claim the attribute was on the map.
  // No results: the plots have no trait colouring, the card holds the
  // attribute alone even if a numeric property was picked as trait.
  const hasResults = useSelector(selectHasResults);
  const hasTrait = hasResults && Boolean(trait.technical_name);
  const hasTraitScale =
    traitColorScale.minValue != null && traitColorScale.maxValue != null;

  // Results select: the traits, then the attributes as one more category of
  // its menu ("Attributes", "Plot ratings"). No results: attributes alone.
  const resultsOptionKeys = useMemo(
    () => [...new Set([...(hasTrait ? traitKeys : []), ...attributeKeys])],
    [hasTrait, traitKeys, attributeKeys]
  );

  const attributeValuesList = selectedAttribute &&
    attributeValues.length > 0 && (
      <div className="attributes d-flex flex-column gap-1">
        {attributeValues.map((value) => (
          <div
            key={value ?? "null"}
            className="legend-value d-flex align-items-center gap-2"
          >
            <span
              className="legend-value__swatch"
              style={{ backgroundColor: getAttributeColor(value) }}
            />
            <span className="text-ellipsis" title={value ?? NULL_GROUP_LABEL}>
              {value ?? NULL_GROUP_LABEL}
            </span>
          </div>
        ))}
      </div>
    );

  // The property printed on each plot, in Plot Map and in Results. Options
  // follow the site: the ID, then the attributes its plots fill in.
  const plotLabelPicker = (
    <div className="d-flex align-items-center gap-2 pt-0">
      Plot labels
      <Select
        className="plot-label-select"
        classNamePrefix="legend-select"
        menuPlacement="auto"
        {...LEGEND_MENU_PROPS}
        isClearable
        isSearchable={false}
        components={{
          IndicatorSeparator: null,
          DropdownIndicator: () => (
            <i className="fa-solid fa-chevron-down property-select-caret" />
          ),
        }}
        placeholder="None"
        options={plotLabelOptions}
        value={
          plotLabelOptions.find(({ value }) => value === plotLabelProperty) ??
          null
        }
        onChange={(option) =>
          dispatch(setPlotLabelProperty(option?.value ?? null))
        }
      />
    </div>
  );

  // Shared by every card: opacity, visibility, folding. `icon`: the header
  // icon's className (the plots grid for the colouring cards), null for the
  // layer's own icon.
  // `opacity`: { value, onChange, disabled } for a slider driving something
  // else than the layer's opacity (the plots card: the plot labels).
  // `visibility`: { shown, onToggle } for an eye hiding something else than
  // the whole layer (the plots card: the plot labels alone). That eye leaves
  // its card open: the card holds the picker of what it hides.
  const renderHeader = (
    collapsed,
    setCollapsed,
    { icon = null, opacity = null, visibility = null } = {}
  ) => {
    const eye = visibility ?? {
      shown: layer.visible,
      onToggle: () =>
        dispatch(upsertMapLayer({ ...layer, visible: !layer.visible })),
    };
    const slider = opacity ?? {
      value: layer.opacity,
      onChange: (value) =>
        dispatch(upsertMapLayer({ ...layer, opacity: value })),
      disabled: !layer.visible,
    };
    return (
      <div className="legend-item-header d-flex align-items-center gap-2 pe-1 ps-2">
        <i
          className={`fa-solid fa-chevron-right clickable discrete-icon
            ${!collapsed ? "fa-rotate-90" : ""}`}
          onClick={() => setCollapsed(!collapsed)}
        />
        <div className="legend-item-vector">
          <i className={icon ?? layer.type.icon} />
        </div>

        <div className="flex-grow-1 ps-1">
          {layer.type.opacityModifiable && (
            <ReactSlider
              disabled={slider.disabled}
              value={slider.value}
              step={0.01}
              min={0}
              max={1}
              onAfterChange={slider.onChange}
              className="cloverfield-slider"
              thumbClassName="cloverfield-slider-thumb"
              trackClassName="cloverfield-slider-track"
            />
          )}
        </div>
        <div className="text-center" style={{ width: "2em" }}>
          <i
            className={`fa-solid discrete-icon clickable ${
              eye.shown ? "fa-eye" : "fa-eye-slash"
            }`}
            onClick={() => {
              if (!visibility) setCollapsed(eye.shown);
              eye.onToggle();
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="powerdash-component legend-item gap-1 mt-2">
        {renderHeader(
          isCollapsed,
          setIsCollapsed,
          layer.type === MAP_LAYER_TYPES.GEOJSON
            ? {
                icon: "fa-solid fa-signs-post",
                opacity: {
                  value: plotLabelOpacity,
                  onChange: (value) => dispatch(setPlotLabelOpacity(value)),
                  disabled: !plotLabelsVisible || !plotLabelProperty,
                },
                visibility: {
                  shown: plotLabelsVisible,
                  onToggle: () =>
                    dispatch(setPlotLabelsVisible(!plotLabelsVisible)),
                },
              }
            : {}
        )}
        {!isCollapsed && (
          <>
            {/* The plots layer (Plot Map and Results, the legend hides it in
                Field Map): the plot labels, the colouring card under. */}
            {layer.type === MAP_LAYER_TYPES.GEOJSON && (
              <div className="legend-choice">{plotLabelPicker}</div>
            )}
            {layer.type === MAP_LAYER_TYPES.ORTHO && (
              <span>{layer.name === "plain" ? "Orthomosaic" : layer.name}</span>
            )}
            {layer.type === MAP_LAYER_TYPES.SCOUTING &&
              scoutingDates[0] &&
              scoutingDates[1] && (
                <div className="d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <Badge
                        color="unset"
                        style={{ backgroundColor: SCOUTING_COLOR }}
                      >
                        {format(scoutingDates[0], "yyyy-MM-dd")}
                      </Badge>{" "}
                      →{" "}
                      <Badge
                        color="unset"
                        style={{ backgroundColor: SCOUTING_COLOR }}
                      >
                        {format(scoutingDates[1], "yyyy-MM-dd")}
                      </Badge>
                    </span>
                    <i
                      className="fa-solid fa-trash clickable discrete-icon"
                      onClick={() => dispatch(resetScouting())}
                      data-tooltip-id="tooltip"
                      data-tooltip-content="Clear scouting layer"
                      data-tooltip-variant="error"
                    />
                  </div>
                  {selectedScoutingTrait && (
                    <span className="mt-2 d-flex align-items-center ">
                      {selectedScoutingTrait.label}
                    </span>
                  )}
                </div>
              )}
            {layer.type === MAP_LAYER_TYPES.SCOUTING &&
              scoutingColorScale &&
              scoutingColorScale.minValue != null &&
              scoutingColorScale.maxValue != null && (
                <WorldMapLegendGrad
                  colorScale={scoutingColorScale}
                  dynamic
                  onChangeStep={(value) =>
                    dispatch(setScoutingColorScaleStep(value))
                  }
                />
              )}
            {/* The trait scale lives in the Results tab's own block; the ortho
                scale stays hidden in the attribute colouring, as before. */}
            {layer.type === MAP_LAYER_TYPES.ORTHO &&
              !isFieldBookMode &&
              !isPlotMapMode &&
              wmsColorScale &&
              wmsColorScale.minValue != null &&
              wmsColorScale.maxValue != null && (
                <WorldMapLegendGrad colorScale={wmsColorScale} />
              )}
          </>
        )}
      </div>
      {/* Results: the trait or attribute colouring, under the labels card.
          Its slider and eye drive the plots layer, like the card above.
          One select for both: the reducer switches the colouring
          (SET_SELECTED_TRAIT to Results, SET_SELECTED_ATTRIBUTE to Field
          Book). */}
      {isResultsPlots && (
        <div className="powerdash-component legend-item gap-1 mt-2">
          {renderHeader(isColoringCollapsed, setIsColoringCollapsed, {
            icon: MAP_LAYER_TYPES.GEOJSON.icon,
          })}
          {!isColoringCollapsed && (
            <div className="legend-choice">
              <PropertySelect
                className="legend-picker"
                classNamePrefix="legend-select"
                caret
                showUnit
                menuPlacement="auto"
                {...LEGEND_MENU_PROPS}
                value={
                  isFieldBookMode ? selectedAttribute : trait.technical_name
                }
                onChange={(option) => {
                  if (!option) return;
                  if (attributeKeys.includes(option.value))
                    dispatch(setSelectedAttribute(option.value));
                  else
                    dispatch(
                      setSelectedTrait(
                        traits.find((t) => t.technical_name === option.value)
                      )
                    );
                }}
                placeholder={
                  hasTrait ? "Select trait or attribute" : "Select attribute"
                }
                optionKeys={resultsOptionKeys}
              />
              {isFieldBookMode ? (
                attributeValuesList
              ) : (
                <>
                  <LayerIdentifier transparent />
                  {hasTraitScale && (
                    <WorldMapLegendGrad
                      colorScale={traitColorScale}
                      dynamic
                      onChangeStep={(value) =>
                        dispatch(setColorScaleStep(value))
                      }
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
      {/* Plot Map: the colouring card, always there. One select like
          Results: picking an attribute puts its colours on the plots, the
          "Plot map" pill of the menu goes back to the blue outlines. Its slider drives the opacity
          of whichever is shown. */}
      {layer.type === MAP_LAYER_TYPES.GEOJSON && isPlotMapMode && (
        <div className="powerdash-component legend-item gap-1 mt-2">
          {renderHeader(isColoringCollapsed, setIsColoringCollapsed, {
            icon: MAP_LAYER_TYPES.GEOJSON.icon,
            opacity: {
              value: plotMapOpacity,
              onChange: (value) => dispatch(setPlotMapOpacity(value)),
              disabled: !layer.visible,
            },
          })}
          {!isColoringCollapsed && (
            <div className="legend-choice">
              <PropertySelect
                className="legend-picker"
                classNamePrefix="legend-select"
                caret
                menuPlacement="auto"
                {...LEGEND_MENU_PROPS}
                value={plotMapColoring ? selectedAttribute : null}
                onChange={(option) => {
                  if (!option) return;
                  if (option.isLeading) {
                    dispatch(setPlotMapColoring(false));
                    return;
                  }
                  dispatch(setSelectedAttribute(option.value));
                  dispatch(setPlotMapColoring(true));
                }}
                placeholder="Plot map"
                leadingOption={{ label: "Plot map", value: "" }}
                optionKeys={attributeKeys}
              />
              {plotMapColoring && attributeValuesList}
            </div>
          )}
        </div>
      )}
    </>
  );
};

WorldMapLegendItem.propTypes = {
  layer: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(MAP_LAYER_TYPES)).isRequired,
    opacity: PropTypes.number.isRequired,
    visible: PropTypes.bool.isRequired,
  }).isRequired,
};
